import type { ComponentPropsWithRef, ReactNode } from 'react'

export type BadgeColor =
    | 'warning'
    | 'warning-ghost'
    | 'secondary'
    | 'secondary-ghost'
    | 'info'
    | 'info-ghost'
    | 'success'
    | 'success-ghost'
    | 'danger'
    | 'danger-ghost'

export type BadgeSize = 'small' | 'medium'

export interface BadgeProps extends ComponentPropsWithRef<'div'> {
    color: BadgeColor
    size?: BadgeSize
    children?: ReactNode
}

const colorClasses: Record<BadgeColor, string> = {
    warning: 'bg-yellow-400 text-black',
    'warning-ghost': 'bg-yellow-50 text-yellow-900',
    secondary: 'bg-gray-500 text-white',
    'secondary-ghost': 'bg-gray-100 text-gray-700',
    info: 'bg-blue-500 text-white',
    'info-ghost': 'bg-blue-50 text-blue-600',
    success: 'bg-active text-white',
    'success-ghost': 'bg-green-50 text-green-900',
    danger: 'bg-error text-white',
    'danger-ghost': 'bg-red-50 text-red-900',
}

const sizeClasses: Record<BadgeSize, string> = {
    small: 'text-xs px-2 py-0.5',
    medium: 'text-sm px-3 py-1',
}

export function Badge({
    color,
    size = 'small',
    children,
    className = '',
    ...restProps
}: BadgeProps) {
    const badgeColorClass = colorClasses[color] ?? ''
    const badgeSizeClass = sizeClasses[size] ?? sizeClasses.small

    return (
        <div
            {...restProps}
            className={`font-medium rounded-lg inline-flex items-center ${badgeColorClass} ${badgeSizeClass} ${className}`.trim()}
        >
            {children}
        </div>
    )
}
